import { useState } from 'react';
import { Box, IconButton, Stack, Typography, Button, Grid2 } from '@mui/material';
import { Field, Form, Formik } from 'formik';
import * as Yup from 'yup';
import { useMutation } from '@tanstack/react-query';
import EditIcon from '@mui/icons-material/Edit';
import CustomDivider from '../../shared/utils/CustomDivider';
import TableFilterTemplate from '../../shared/utils/TableFilterTemplate';
import { NormalContainerWithHeader } from '../../shared/components/student/NormalComponent';
import ToggleSwitch from '../../shared/utils/CustomToggle';
import { postLmsLanguage } from '../../assets/nodeApi/postApi/postAdminApi';
import { putLmsLanguage } from '../../assets/nodeApi/putApi/putAdminApi';
import { patchLmsLanguage } from '../../assets/nodeApi/patchApi/PatchAdminApi';
import { Grid_Gap, Input_Width } from '../../shared/constants';
import { useToaster } from '../../shared/utils/ToasterProvider';
import { TextFormField } from '../../shared/components/formfields/TextFormField';
import { queryClient } from '../../assets/nodeApi/reactQueryClient';
import { useLmsDropdownOptions } from './option/LsmOption';

const LmsLanguage = () => {
  const { showToaster } = useToaster();
  const [selectedGrade, setSelectedGrade] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const { lmsLangauge } = useLmsDropdownOptions()
  const validationSchema = Yup.object().shape({
    name: Yup.string().required('Name is required'),
    code: Yup.string().required('Code is required'),
    direction: Yup.string().required('Direction Email is required')
  });

  const initialValues = {
    name: selectedGrade?.name || '',
    code: selectedGrade?.code || '',
    direction: selectedGrade?.direction || '',

  };

  const mutationAdd = useMutation({
    mutationFn: (formData) => postLmsLanguage(formData),
    onSuccess: () => {
      showToaster('Langauge added successfully!', '', 'success');
      queryClient.invalidateQueries(['lms/languages']);
    },
    onError: (err) => {
      showToaster(err?.response?.data?.error || 'Failed to add Langauge', '', 'error');
    },
  });

  // Update grade
  const mutationUpdate = useMutation({
    mutationFn: ({ formData, id }) => putLmsLanguage(formData, id),
    onSuccess: () => {
      showToaster('Langauge updated successfully!', '', 'success');
      queryClient.invalidateQueries(['lms/languages']);
    },
    onError: (err) => {
      showToaster(err?.response?.data?.error || 'Failed to update status', '', 'error');
    },
  });

  const handleSubmit = (values, { resetForm }) => {
   
    const payload = {
      ...values
    };
  
    if (isEditing) {
      mutationUpdate.mutate({ formData: payload, id: selectedGrade.id, });
    } else {
      mutationAdd.mutate(payload);
      resetForm();
    }
    setIsEditing(false);
    setSelectedGrade(null);
  };

  const toggleStatusMutation = useMutation({
    mutationFn: (formData, id) => patchLmsLanguage(formData, id),
    onSuccess: (response) => {
      showToaster(`Language ${response?.data?.status === "1" ? "Enabled" : "Disabled"} successfully!`, '', 'success');
      queryClient.invalidateQueries(['lms/languages']);
    },
    onError: (err) => {
      showToaster(err?.response?.data?.error || 'Failed to update Language', '', 'error');
    },
  });



  const gradeColumns = [
    { field: 'sr_no', headerName: 'Sr No', flex: 1, renderCell: (params) => <Typography>{params.value}</Typography> },
    { field: 'name', headerName: 'Name', flex: 1, renderCell: (params) => <Typography>{params.value}</Typography> },
    { field: 'code', headerName: 'Code', flex: 1, renderCell: (params) => <Typography>{params.value}</Typography> },
    { field: 'direction', headerName: 'Direction', flex: 1, renderCell: (params) => <Typography>{params.value}</Typography> },
    {
      field: 'actionsEdit',
      headerName: 'Edit',
      flex: 1,
      renderCell: (params) => (
        <Stack direction="row" spacing={1}>
          <IconButton
            onClick={() => {
              setSelectedGrade(params.row);
              setIsEditing(true);
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            size="small"
          >
            <EditIcon />
          </IconButton>
        </Stack>
      ),
    },
    {
      field: 'actionsToggle',
      headerName: 'Status',
      flex: 1,
      renderCell: (params) => (
        <ToggleSwitch checked={params.row.status === "1" ? true : false} onCheck={() => toggleStatusMutation.mutate(params?.row?.id)} />
      ),
    },
  ];

  return (
    <Box>
      <CustomDivider />
      <NormalContainerWithHeader headerText={isEditing ? 'Edit Language' : 'Add Language'}>
        <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit} enableReinitialize>
          {({ resetForm }) => (
            <Form>
              <Grid2 container spacing={Grid_Gap}>
                <Grid2 children size={Input_Width}>
                  <Field name="name"
                    component={TextFormField}
                    label="Name"
                    isRequired
                  />
                </Grid2>

                <Grid2 children size={Input_Width}>
                  <Field
                    name="code"
                    component={TextFormField}
                    label="Code"
                    isRequired
                  />
                </Grid2>
                <Grid2 children size={Input_Width}>
                  <Field
                    name="direction"
                    component={TextFormField}
                    label="Direction"
                    isRequired
                  />
                </Grid2>
              </Grid2>
              <CustomDivider />
              <Grid2 container spacing={Grid_Gap} sx={{justifyContent:"end"}}>
                <Grid2 children size={{ xs: 12, sm: 6, md: 3 }}>
                  <Button
                    variant="outlined"
                    type="button"
                    onClick={() => {
                      resetForm();
                      setIsEditing(false);
                      setSelectedGrade(null);
                    }}
                    fullWidth
                  >
                    {isEditing ? 'Cancel' : 'Reset'}
                  </Button>
                </Grid2>
                <Grid2 children size={{ xs: 12, sm: 6, md: 3 }}>
                  <Button
                    variant="contained"
                    type="submit"
                    fullWidth
                    disabled={mutationAdd.isPending || mutationUpdate.isPending}
                  >
                    {mutationUpdate.isPending
                      ? "Updating..."
                      : mutationAdd.isPending
                        ? "Submitting..."
                        : isEditing
                          ? "Update"
                          : "Submit"}
                  </Button>
                </Grid2>
              </Grid2>
            </Form>
          )}
        </Formik>
      </NormalContainerWithHeader>

      <CustomDivider />

      <TableFilterTemplate
        title="Language List"
        columns={gradeColumns}
        rowsData={lmsLangauge.options || []}
        isLoading={lmsLangauge.isLoading} />
    </Box>
  );
};

export default LmsLanguage;


